<script setup lang="ts">
import { computed, onBeforeMount, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

import { ModalName } from '@/constants/modals';
import { getFormattedHour } from "@/helpers/date";
import { concat } from "@/helpers/string";
import missionsService from "@/modules/missions/services/missionsService";
import { MissionStatus, WorkerMissionStatus } from '@/types/mission';

import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";

interface Props {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    cell: any;
    context: string;
}

const props = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits(["toggle", "refresh", "close"]);

const modalStore = useModalStore();
const zoneHeight = 26.5;

const zone = computed(() => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    const z = props.cell.unavailabilities?.zones && props.cell.unavailabilities?.zones[props.context];
    return z;
});

const zones = computed(() => {
    const z = props.cell.unavailabilities?.zones;
    return z;
});

const mission = computed(() => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    if (zone.value !== undefined && zone.value !== null && zone.value?.clientName) {
        return zone.value;
    }
    return null;
});

const hasMission = computed(() => {
    return !!mission.value;
});

const hasContext = computed(() => {
    const val = zone.value === false || zone.value?.clientName || zone.value?.value === false;
    return val;
});

const isNextContext = computed(() => {
    switch (props.context) {
        case "afternoon":
            return mission.value && contextHasMission("morning");
        case "evening":
            return mission.value && contextHasMission("afternoon");
        case "night":
            return mission.value && contextHasMission("evening");
        default:
            return false;
    }
});

const size = computed(() => {
    let contexts = ["morning", "afternoon", "evening", "night"];
    let size = 0;
    let chaining: number[] = [];

    if (mission.value) {
        contexts.forEach((context: string, index: number) => {
            if (contextHasMission(context)) {
                if (!chaining.length || (index && chaining.includes(index - 1))) {
                    size += zoneHeight;
                }
                chaining.push(index);
            }
        });
    }

    return `${size > 0 ? size : zoneHeight}px`;
});

const missionContext = computed(() => {
    if (mission.value) {
        if (
            mission.value.status === MissionStatus.evaluation ||
            mission.value.status === MissionStatus.terminated ||
            mission.value.status === MissionStatus.archived
        ) {
            return "black";
        }
        if (mission.value.workerStatus === WorkerMissionStatus.CONFIRMED) {
            return "teal";
        }
        if (mission.value.workerStatus === WorkerMissionStatus.PROPOSED) {
            return "orange";
        }
        if (mission.value.workerStatus === WorkerMissionStatus.ACCEPTED) {
            return "green";
        }
        return "grey";
    }
    return "";
});

const isOtherAgencyMission = computed(() => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return Object.values(zones.value || {}).some((e: any) => e.isOtherAgencyMission);
});

const isSameAgencyBrand = computed(() => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return Object.values(zones.value || {}).some((e: any) => e.isSameBrand);
});

const missionType = computed((): { missionStatus: string; missionStatusDetail: string } => {
    if (!mission.value) {
        return {
            missionStatus: "",
            missionStatusDetail: "",
        };
    }

    if (mission.value.workerStatus === WorkerMissionStatus.ACCEPTED) {
        return {
            missionStatus: t("page.mission.missionStatus.accepted") as string,
            missionStatusDetail: t("page.mission.missionStatus.missionStatusOnOtherAgency", {
                missionStatus: t("page.mission.missionStatus.accepted"),
            }) as string,
        };
    }
    if (mission.value.workerStatus === WorkerMissionStatus.CONFIRMED) {
        return {
            missionStatus: t("page.mission.missionStatus.confirmed") as string,
            missionStatusDetail: t("page.mission.missionStatus.missionStatusOnOtherAgency", {
                missionStatus: t("page.mission.missionStatus.confirmed"),
            }) as string,
        };
    }
    if (mission.value.workerStatus === WorkerMissionStatus.PROPOSED) {
        return {
            missionStatus: t("page.mission.missionStatus.proposed") as string,
            missionStatusDetail: t("page.mission.missionStatus.missionStatusOnOtherAgency", {
                missionStatus: t("page.mission.missionStatus.proposed"),
            }) as string,
        };
    }

    return {
        missionStatus: "",
        missionStatusDetail: "",
    };
});

const contextHasMission = (context: string) => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    return !!(zones.value?.[context] && zones.value[context].clientName && zones.value[context].id === mission.value?.id);
};

const handleRefresh = () => {
    emit("refresh");
};

const handleClose = () => {
    emit("close");
};

const showMissionDetails = async () => {
    if (!mission.value?.id) return;
    const missionFetched = await missionsService.getMission(mission.value.id);
    modalStore.actions.open(ModalName.MissionDetails, { mission: missionFetched });
};

onBeforeMount(() => {
    modalStore.actions.injectManyProps([
        {
            modal: ModalName.MissionDetails,
            payload: {
                onRefresh: handleRefresh,
                onUpdate: handleRefresh,
                onCancel: handleRefresh,
                onClose: handleClose,
            },
        },
        {
            modal: ModalName.CancelMissionLegacy,
            payload: {
                onCancel: handleRefresh,
                onRefresh: handleRefresh,
            },
        },
    ]);
});

onUnmounted(() => {
    modalStore.actions.resetInjectedProps();
});
</script>

<template>
    <div
        :class="['AvailabilityZone', missionContext, { mission: hasMission }]"
        v-if="!isNextContext && context !== 'reason' && context !== 'comment'"
        :style="{ minHeight: size, maxHeight: size, height: size }"
    >
        <!-- is in mission -->
        <div v-if="hasContext && hasMission && !isOtherAgencyMission" :class="[context, 'mission']" @click="emit('toggle')">
            <div class="missionName" v-if="mission?.clientName" @click="showMissionDetails">
                <div class="name">
                    <div class="round" />
                    <div class="text" :title="mission.clientName">{{ mission.clientName }}</div>
                </div>
                <div class="hours">
                    <div class="hour" v-for="(hour, index) in mission.hours" :key="hour">
                        {{ getFormattedHour(hour.start) }} - {{ getFormattedHour(hour.end) }}
                    </div>
                </div>
            </div>
        </div>

        <!-- mission in other agency -->
        <div v-else-if="hasContext && hasMission && isOtherAgencyMission" class="zone unavailable information">
            <div class="infos">
                <Dropdown>
                    <template #trigger-zone>
                        <icon icon="info" />
                    </template>
                    <template #dropdown-content>
                        <div class="popoverContent">
                            <p class="main">{{ missionType.missionStatusDetail }}</p>
                            <p class="item">
                                {{ concat([t("form.agency"), zones[context].missionAgency.name, zones[context].missionAgency.address.city]) }}
                            </p>
                        </div>
                    </template>
                </Dropdown>
            </div>
            <p v-if="isSameAgencyBrand" class="name">{{ missionType.missionStatus }}</p>
            <p v-else class="name">{{ t("global.unavailable") }}</p>
        </div>

        <!-- is available -->
        <div v-else-if="!hasContext && !hasMission" class="zone available" @click="emit('toggle')">
            <div>{{ t(`page.interim.columnsAvailabilities.${context}`) }}</div>
        </div>

        <!-- is not available (normal unavailability) -->
        <div v-else-if="hasContext" class="zone unavailable" @click="emit('toggle')">
            <div>{{ t(`page.interim.columnsAvailabilities.${context}`) }}</div>
        </div>
    </div>
</template>

<style lang="scss">
.AvailabilityZone {
    display: flex;
    flex-direction: column;
    width: 100%;

    &.mission {
        flex-grow: 1;
    }

    .zone {
        margin-bottom: 5px;
        border-radius: 20px;
        color: #fff;
        font-size: 12px;
        padding: 3.75px 7.5px;
        margin-right: 5px;
        align-self: baseline;
        width: 100%;
        border: 1px solid transparent;
        height: 100%;
        text-align: left;
        position: relative;
        .infos {
            cursor: pointer;
            position: absolute;
            right: 6px;
            top: 50%;
            transform: translateY(-50%);

            fill: $grey2;

            &:hover {
                fill: $black;
            }
        }

        & > div {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &.available {
            background: $lightgrey3;
            color: $grey;
        }
        &.unavailable {
            background: $grey2;
            color: $grey;
            opacity: 0.7;
        }
        &.information {
            padding: 0.3rem;
            border: 1px $grey2 solid;
            background-color: $white;
            opacity: 1;
            overflow: hidden;
            border-radius: 10px;

            p {
                margin: 0 0 1.2rem 0.4rem;

                &.name {
                    color: black;
                    font-weight: $bold;
                    font-size: 1rem;
                }
            }
        }
    }

    .missionOtherAgency {
        padding: calc(#{$padding} / 6);
        font-weight: $bold;
    }

    .missionName {
        cursor: pointer;
        color: $lightblack;
        font-weight: $medium;
        background: $white;
        padding: 3px calc($padding / 4);
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        height: 100%;

        .name {
            font-size: 16px;
            display: flex;
            align-items: center;
            width: 100%;

            .round {
                min-width: 12px;
                max-width: 12px;
                min-height: 12px;
                max-height: 12px;
                border-radius: 100%;
                margin-right: calc($padding / 3);
            }

            .text {
                width: calc(100% - 12px - 10px);
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }

            &:first-letter {
                text-transform: uppercase;
            }
        }
    }

    .mission {
        margin-bottom: calc($padding / 6);
        height: 100%;
        width: 100%;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid $lightgrey;
    }

    &.mission {
        &.black {
            .round {
                background: $black;
            }
        }
        &.teal {
            .round {
                background: $teal;
            }
        }
        &.purple {
            .round {
                background: $purple;
            }
        }
        &.orange {
            .round {
                background: $orange;
            }
        }
        &.grey {
            .round {
                background: $grey;
            }
        }
        &.green {
            .round {
                background: $success-600;
            }
        }
    }

    .hours {
        margin-top: calc($padding / 6);
        .hour {
            color: $grey;
            font-size: 11px;
            margin-bottom: calc($padding / 8);

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    &.edit {
        .zone {
            cursor: pointer;
            transition: 0.2s linear;

            &.mission {
                cursor: default;
            }

            &:hover {
                opacity: 0.7;
            }
        }
    }
}

.dropdown-content {
    .popoverContent {
        .main {
            font-weight: $bold;
        }

        .item {
            margin-bottom: 0.8rem;
        }
    }
}
</style>
